import React, { useEffect, useRef } from 'react'
import {
  Settings,
  X,
  Radio,
  Layers,
  BookOpen,
  FileCode,
  Zap,
  Server
} from 'lucide-react'
import ProviderSelector from './chat/ProviderSelector'
import { Provider } from '../services/chatService'

interface AdminSettingsPanelProps {
  isOpen: boolean
  onClose: () => void

  // Streaming settings
  streamingEnabled: boolean
  onStreamingToggle: (enabled: boolean) => void

  // Prompt version settings
  promptVersion: string
  isPromptSelectorOpen: boolean
  onPromptVersionChange: (version: string) => void
  onPromptSelectorToggle: () => void

  // Provider settings
  providers: Provider[]
  currentProvider: Provider | null
  switchProvider: (provider: Provider) => void
  isLoadingProviders: boolean

  // Language-pair prompt settings (passed from parent to share state)
  enableLanguagePairPrompts: boolean
  onLanguagePairPromptsToggle: (enabled: boolean) => void
}

const AdminSettingsPanel: React.FC<AdminSettingsPanelProps> = ({
  isOpen,
  onClose,
  streamingEnabled,
  onStreamingToggle,
  promptVersion,
  isPromptSelectorOpen,
  onPromptVersionChange,
  onPromptSelectorToggle,
  providers,
  currentProvider,
  switchProvider,
  isLoadingProviders,
  enableLanguagePairPrompts,
  onLanguagePairPromptsToggle
}) => {
  const panelRef = useRef<HTMLDivElement>(null)

  // DEBUG: Log current value when panel opens
  useEffect(() => {
    if (isOpen) {
      console.log('🎛️ [AdminSettingsPanel] Panel opened, current state:', {
        enableLanguagePairPrompts,
        timestamp: new Date().toISOString()
      })
    }
  }, [isOpen, enableLanguagePairPrompts])

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }

    window.addEventListener('keydown', handleEscape)
    return () => window.removeEventListener('keydown', handleEscape)
  }, [isOpen, onClose])

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onClose()
      }
    }

    if (isOpen) {
      // Delay to avoid closing immediately after opening
      setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside)
      }, 100)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, onClose])

  if (!isOpen) return null

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/20 z-40"
        onClick={onClose}
      />

      {/* Panel */}
      <div
        ref={panelRef}
        className="fixed right-4 top-20 w-80 bg-white rounded-xl shadow-2xl border border-gray-200 z-50 overflow-hidden"
        data-testid="admin-settings-panel"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-50 to-amber-50 px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Settings className="w-4 h-4 text-orange-600" />
            <h3 className="font-semibold text-gray-900">Admin Settings</h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-white/50 rounded-md transition-colors"
            aria-label="Close admin settings panel"
          >
            <X className="w-4 h-4 text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-6 max-h-[calc(100vh-12rem)] overflow-y-auto">

          {/* Provider Section - MOVED TO TOP to avoid dropdown clipping */}
          <section>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <Server className="w-3.5 h-3.5" />
              AI Provider
            </h4>

            <div>
              <ProviderSelector
                providers={providers}
                currentProvider={currentProvider}
                onProviderChange={switchProvider}
                isLoading={isLoadingProviders}
              />
            </div>
          </section>

          {/* Performance Section */}
          <section>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <Zap className="w-3.5 h-3.5" />
              Performance
            </h4>

            <div className="space-y-3">
              {/* Streaming Toggle */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Response Mode
                </label>
                <div className="flex items-center bg-gray-100 rounded-lg p-1">
                  <button
                    data-testid="admin-streaming-enabled"
                    data-state={streamingEnabled ? 'active' : 'inactive'}
                    aria-label="Enable streaming mode for progressive HTML generation"
                    onClick={() => onStreamingToggle(true)}
                    className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 flex items-center justify-center gap-1.5 ${
                      streamingEnabled
                        ? 'bg-white text-green-700 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <Radio className={`w-3.5 h-3.5 ${streamingEnabled ? 'text-green-500' : ''}`} />
                    <span>Stream</span>
                  </button>
                  <button
                    data-testid="admin-streaming-disabled"
                    data-state={!streamingEnabled ? 'active' : 'inactive'}
                    aria-label="Disable streaming mode for complete HTML generation"
                    onClick={() => onStreamingToggle(false)}
                    className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 flex items-center justify-center gap-1.5 ${
                      !streamingEnabled
                        ? 'bg-white text-purple-700 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <Layers className={`w-3.5 h-3.5 ${!streamingEnabled ? 'text-purple-500' : ''}`} />
                    <span>Full</span>
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {streamingEnabled
                    ? 'Progressive HTML generation with real-time updates'
                    : 'Complete HTML generation in single response'
                  }
                </p>
              </div>
            </div>
          </section>

          {/* Prompts Section */}
          <section>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <FileCode className="w-3.5 h-3.5" />
              Prompts
            </h4>

            <div className="space-y-3">
              {/* Language-Pair Toggle */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Prompt Strategy
                </label>
                <div className="flex items-center bg-gray-100 rounded-lg p-1">
                  <button
                    data-testid="admin-langpair-enabled"
                    data-state={enableLanguagePairPrompts ? 'active' : 'inactive'}
                    aria-label="Enable lens guides for 2-3x faster responses"
                    onClick={() => {
                      console.log('🔘 [AdminSettingsPanel] Lens Guide button clicked - setting to TRUE')
                      onLanguagePairPromptsToggle(true)
                    }}
                    className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 flex items-center justify-center gap-1.5 ${
                      enableLanguagePairPrompts
                        ? 'bg-white text-amber-700 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <BookOpen className={`w-3.5 h-3.5 ${enableLanguagePairPrompts ? 'text-amber-500' : ''}`} />
                    <span>Lens Guide</span>
                  </button>
                  <button
                    data-testid="admin-langpair-disabled"
                    data-state={!enableLanguagePairPrompts ? 'active' : 'inactive'}
                    aria-label="Disable lens guides and use generic prompts"
                    onClick={() => {
                      console.log('🔘 [AdminSettingsPanel] Generic button clicked - setting to FALSE')
                      onLanguagePairPromptsToggle(false)
                    }}
                    className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 flex items-center justify-center gap-1.5 ${
                      !enableLanguagePairPrompts
                        ? 'bg-white text-gray-700 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <FileCode className={`w-3.5 h-3.5 ${!enableLanguagePairPrompts ? 'text-gray-500' : ''}`} />
                    <span>Generic</span>
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {enableLanguagePairPrompts
                    ? 'Uses specialized lens guides for supported pairs (2-3x faster)'
                    : 'Uses generic prompts for all language pairs'
                  }
                </p>
              </div>

              {/* Prompt Version Selector */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Prompt Version
                </label>
                <div className="relative">
                  <button
                    data-testid="admin-prompt-selector-button"
                    onClick={onPromptSelectorToggle}
                    className="w-full px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700 flex items-center justify-between transition-colors"
                  >
                    <span>{promptVersion.toUpperCase()}</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {isPromptSelectorOpen && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden z-10">
                      {['v1', 'v2', 'v3'].map((version) => (
                        <button
                          key={version}
                          onClick={() => {
                            onPromptVersionChange(version)
                            onPromptSelectorToggle()
                          }}
                          className={`w-full px-3 py-2 text-sm text-left hover:bg-gray-50 transition-colors ${
                            promptVersion === version ? 'bg-orange-50 text-orange-700 font-medium' : 'text-gray-700'
                          }`}
                        >
                          {version.toUpperCase()}
                          {version === 'v1' && <span className="text-xs text-gray-500 ml-2">(Default)</span>}
                          {version === 'v3' && <span className="text-xs text-gray-500 ml-2">(Agentic)</span>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>

        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-4 py-2 border-t border-gray-200">
          <p className="text-xs text-gray-500 text-center">
            🔓 Admin Mode Active
          </p>
        </div>
      </div>
    </>
  )
}

export default AdminSettingsPanel
